import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import {
  ClerkLoaded,
  ClerkLoading,
  ClerkProvider,
  RedirectToSignIn,
  SignedIn,
  SignedOut,
} from "@clerk/clerk-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Landing } from "@/routes/Landing";
import { Dashboard } from "@/routes/Dashboard";
import { Briefings } from "@/routes/Briefings";
import { Opportunities } from "@/routes/Opportunities";
import { Onboarding } from "@/routes/Onboarding";
import { Library } from "@/routes/Library";
import { CollectionDetail } from "@/routes/CollectionDetail";
import { WeeklyReset } from "@/routes/WeeklyReset";
import { Circles } from "@/routes/Circles";
import { CircleDetail } from "@/routes/CircleDetail";
import { AdminOverview } from "@/routes/admin/Overview";
import { AdminSources } from "@/routes/admin/Sources";
import { AdminSynthesis } from "@/routes/admin/Synthesis";
import { AdminOpportunities } from "@/routes/admin/AdminOpportunities";
import { AdminCircles } from "@/routes/admin/AdminCircles";
import { AppShell } from "@/components/layout/AppShell";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AdminGuard } from "@/components/admin/AdminGuard";

const clerkKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
if (!clerkKey) {
  // Fail loud during boot — better than mysterious 401s later.
  console.error("Missing VITE_CLERK_PUBLISHABLE_KEY in .env");
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, refetchOnWindowFocus: false, retry: 1 },
  },
});

export default function App() {
  return (
    <ClerkProvider publishableKey={clerkKey ?? ""} afterSignInUrl="/app" afterSignUpUrl="/onboarding">
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route
              path="/onboarding"
              element={
                <Authed>
                  <Onboarding />
                </Authed>
              }
            />
            <Route
              path="/app"
              element={
                <Authed>
                  <AppShell />
                </Authed>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="briefings" element={<Briefings />} />
              <Route path="opportunities" element={<Opportunities />} />
              <Route path="library" element={<Library />} />
              <Route path="library/:slug" element={<CollectionDetail />} />
              <Route path="rhythm/weekly" element={<WeeklyReset />} />
              {/* Old direct URLs — bookmark-safe redirects to Library. */}
              <Route path="saved" element={<Navigate to="/app/library" replace />} />
              <Route path="follows" element={<Navigate to="/app/library" replace />} />
              <Route path="circles" element={<Circles />} />
              <Route path="circles/:slug" element={<CircleDetail />} />
              {/* Hidden surfaces — direct URLs redirect to Intelligence. */}
              <Route path="narratives" element={<Navigate to="/app" replace />} />
              <Route path="chapters" element={<Navigate to="/app" replace />} />
              <Route path="command" element={<Navigate to="/app" replace />} />
              <Route path="admin" element={<AdminGuard><AdminOverview /></AdminGuard>} />
              <Route path="admin/sources" element={<AdminGuard><AdminSources /></AdminGuard>} />
              <Route path="admin/synthesis" element={<AdminGuard><AdminSynthesis /></AdminGuard>} />
              <Route path="admin/opportunities" element={<AdminGuard><AdminOpportunities /></AdminGuard>} />
              <Route path="admin/circles" element={<AdminGuard><AdminCircles /></AdminGuard>} />
            </Route>
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </ClerkProvider>
  );
}

function Authed({ children }: { children: React.ReactNode }) {
  return (
    <>
      <ClerkLoading>
        <AppLoading />
      </ClerkLoading>
      <ClerkLoaded>
        <SignedIn>
          <ErrorBoundary>{children}</ErrorBoundary>
        </SignedIn>
        <SignedOut>
          <RedirectToSignIn />
        </SignedOut>
      </ClerkLoaded>
    </>
  );
}

function AppLoading() {
  return (
    <div className="min-h-dvh flex items-center justify-center bg-ink">
      <span className="font-display text-sm tracking-[0.3em] text-muted animate-pulse">
        CONNECT
      </span>
    </div>
  );
}
